import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStytchMemberSession } from '@stytch/react/b2b';
import Axios from 'axios';

import { Spinner } from 'components/ui/spinner';
import { getSessionOrganizations } from 'lib/auth';
import {
    setObservedStytchOrganizationId,
    subscribeToTenantSessionEvents,
} from 'lib/tenant-session';

const isTenantTransitionRoute = () =>
    window.location.pathname === '/switch-organization' ||
    window.location.pathname === '/authenticate';

export const TenantSessionBoundary = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const queryClient = useQueryClient();
    const { session } = useStytchMemberSession();
    const baselineOrganizationId = useRef<string | null>(null);
    const [blocked, setBlocked] = useState(false);
    const currentOrganizationId = session?.organization_id ?? null;
    const isTransitionRoute = isTenantTransitionRoute();

    if (!baselineOrganizationId.current && currentOrganizationId) {
        baselineOrganizationId.current = currentOrganizationId;
        setObservedStytchOrganizationId(currentOrganizationId);
    }

    const sessionChanged = Boolean(
        baselineOrganizationId.current &&
            currentOrganizationId &&
            baselineOrganizationId.current !== currentOrganizationId,
    );

    useEffect(() => {
        if (!sessionChanged || isTransitionRoute) return;
        setBlocked(true);
        void queryClient
            .cancelQueries()
            .catch(() => undefined)
            .then(() => {
                queryClient.clear();
                window.location.replace('/app');
            });
    }, [isTransitionRoute, queryClient, sessionChanged]);

    useEffect(
        () =>
            subscribeToTenantSessionEvents((event) => {
                if (event.type === 'switch-started') {
                    setBlocked(true);
                    void queryClient.cancelQueries();
                    queryClient.clear();
                    return;
                }
                window.location.replace('/app');
            }),
        [queryClient],
    );

    useEffect(() => {
        const validateVisibleTenant = async () => {
            if (
                document.visibilityState !== 'visible' ||
                !baselineOrganizationId.current ||
                isTenantTransitionRoute()
            ) {
                return;
            }
            try {
                const response = await getSessionOrganizations();
                if (
                    response.current_stytch_organization_id !==
                    baselineOrganizationId.current
                ) {
                    setBlocked(true);
                    await queryClient.cancelQueries();
                    queryClient.clear();
                    window.location.replace('/app');
                }
            } catch (error) {
                if (
                    Axios.isAxiosError(error) &&
                    (error.response?.status === 401 ||
                        error.response?.status === 403)
                ) {
                    setBlocked(true);
                    await queryClient.cancelQueries();
                    queryClient.clear();
                    window.location.replace('/app');
                    return;
                }
                // Existing server-side tenant checks still reject stale writes.
                // Avoid a reload loop for transient connectivity failures.
            }
        };
        const onVisibilityChange = () => void validateVisibleTenant();
        window.addEventListener('focus', onVisibilityChange);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('focus', onVisibilityChange);
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
        };
    }, [queryClient]);

    if ((blocked || sessionChanged) && !isTransitionRoute) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Spinner size="sm" /> Updating your organization session…
                </div>
            </div>
        );
    }

    return children;
};
